import React, { useCallback, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  Link,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { ErrorGroup, ErrorKind, ErrorWindow, ErrorsReport, insightsApi } from '../../services/insightsApi';
import { SectionTitle, StatCard, fmtBucket, fmtNum, fmtPct } from '../performance/common';
import { KIND_META, Sparkline, TargetLink, ago, fmtDateTime, links, useReport } from './common';

const WINDOWS: { value: ErrorWindow; label: string }[] = [
  { value: '24h', label: '24h' },
  { value: '7d', label: '7d' },
  { value: '30d', label: '30d' },
];
const SHOWN_TARGETS = 3;

const KindChip: React.FC<{ kind: ErrorKind; count?: number; selected?: boolean; onClick?: () => void }> = ({ kind, count, selected, onClick }) => {
  const meta = KIND_META[kind];
  return (
    <Tooltip title={meta.help}>
      <Chip
        size="small"
        label={count === undefined ? meta.label : `${meta.label} · ${fmtNum(count)}`}
        onClick={onClick}
        variant={selected ? 'filled' : 'outlined'}
        sx={{
          borderColor: meta.color,
          color: selected ? '#fff' : meta.color,
          bgcolor: selected ? meta.color : undefined,
          '&&:hover': { bgcolor: selected ? meta.color : undefined },
          fontWeight: 500,
        }}
      />
    </Tooltip>
  );
};

const GroupTable: React.FC<{
  groups: ErrorGroup[];
  axisLabels: string[];
  customerId?: string | null;
  unit: string;
  countLabel: string;
}> = ({ groups, axisLabels, customerId, unit, countLabel }) => (
  <Box sx={{ overflowX: 'auto' }}>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Error</TableCell>
          <TableCell align="right">{countLabel}</TableCell>
          <TableCell>Over time</TableCell>
          <TableCell>Affected</TableCell>
          <TableCell>Seen</TableCell>
          <TableCell>Examples</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {groups.map(g => {
          const meta = KIND_META[g.kind];
          const shown = g.targets.slice(0, SHOWN_TARGETS);
          const hidden = g.targets.slice(SHOWN_TARGETS);
          const hiddenCount = hidden.length + g.more_targets;
          return (
            <TableRow key={g.id} sx={{ verticalAlign: 'top' }}>
              <TableCell sx={{ maxWidth: 420 }}>
                <Stack spacing={0.5}>
                  <Box>
                    <KindChip kind={g.kind} />
                  </Box>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12.5, wordBreak: 'break-word' }}>
                    {g.label}
                  </Typography>
                </Stack>
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {fmtNum(g.count)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {fmtPct(g.share)}
                </Typography>
              </TableCell>
              <TableCell>
                <Sparkline values={g.series} labels={axisLabels} color={meta.color} unit={unit} />
              </TableCell>
              <TableCell sx={{ minWidth: 180 }}>
                <Stack spacing={0.25}>
                  {shown.map(t => (
                    <Stack key={`${t.type}:${t.key}`} direction="row" spacing={0.75} alignItems="center">
                      <Chip size="small" variant="outlined" label={t.type} sx={{ height: 18, fontSize: 10.5 }} />
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                        <TargetLink type={t.type} id={t.id} name={t.name} customerId={customerId} />
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {fmtNum(t.count)}
                      </Typography>
                    </Stack>
                  ))}
                  {hiddenCount > 0 && (
                    <Tooltip title={hidden.map(t => `${t.name} (${t.count})`).join(', ')}>
                      <Typography variant="caption" color="text.secondary" sx={{ cursor: 'help' }}>
                        and {hiddenCount} more
                      </Typography>
                    </Tooltip>
                  )}
                </Stack>
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Tooltip title={`First seen ${fmtDateTime(g.first_seen)} · last seen ${fmtDateTime(g.last_seen)}`}>
                  <Box sx={{ cursor: 'help' }}>
                    <Typography variant="body2">{ago(g.last_seen)}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      first {ago(g.first_seen)}
                    </Typography>
                  </Box>
                </Tooltip>
              </TableCell>
              <TableCell>
                <Stack spacing={0.25}>
                  {g.examples.map((e, i) => (
                    <Link key={e.id} component={RouterLink} to={links.run(e.type, e.id)} underline="hover" variant="body2">
                      {e.type === 'chain' ? 'Chain run' : 'Run'} {i + 1}
                    </Link>
                  ))}
                </Stack>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  </Box>
);

export const ErrorExplorer: React.FC<{ customerId?: string | null }> = ({ customerId }) => {
  const { getIdToken } = useAuth();
  const api = useMemo(() => insightsApi(getIdToken), [getIdToken]);
  const [range, setRange] = useState<ErrorWindow>('7d');
  const [includeRescued, setIncludeRescued] = useState(false);
  const [kind, setKind] = useState<ErrorKind | null>(null);
  const load = useCallback(() => api.errors(range, includeRescued, customerId), [api, range, includeRescued, customerId]);
  const { data, loading, error } = useReport<ErrorsReport>(load);

  const axisLabels = useMemo(() => (data ? data.axis.map(b => fmtBucket(b, data.window.unit)) : []), [data]);
  const groups = useMemo(() => (data?.groups ?? []).filter(g => !kind || g.kind === kind), [data, kind]);
  const rescuedGroups = useMemo(() => (data?.rescued?.groups ?? []).filter(g => !kind || g.kind === kind), [data, kind]);

  const t = data?.totals;
  const functionRate = t && t.function_runs ? t.function_failed / t.function_runs : null;
  const chainRate = t && t.chain_runs ? t.chain_failed / t.chain_runs : null;

  return (
    <Box sx={{ opacity: loading && data ? 0.6 : 1, transition: 'opacity 120ms' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 760 }}>
          Failed runs across all your functions and chains, grouped by error type. Messages are normalized (ids, numbers, links and quoted
          values removed) so the same problem from different runs lands in one group. Function runs that were steps of a chain count once,
          as their chain's failure.
        </Typography>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Tooltip title="Also show successful runs where a model call failed first and a retry or backup model answered.">
            <FormControlLabel
              control={<Switch size="small" checked={includeRescued} onChange={e => setIncludeRescued(e.target.checked)} />}
              label={<Typography variant="body2">Rescued failures</Typography>}
            />
          </Tooltip>
          <ToggleButtonGroup size="small" exclusive value={range} onChange={(_, v) => v && setRange(v)}>
            {WINDOWS.map(w => (
              <ToggleButton key={w.value} value={w.value}>
                {w.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}
      {!data && loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {data && t && (
        <>
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            <Grid item xs={6} md={3}>
              <StatCard label="Failed runs" value={fmtNum(t.failed)} caption={`${fmtNum(t.function_runs + t.chain_runs)} runs in total`} />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatCard
                label="Function runs failed"
                value={fmtPct(functionRate)}
                caption={`${fmtNum(t.function_failed)} of ${fmtNum(t.function_runs)} direct runs`}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatCard
                label="Chain runs failed"
                value={fmtPct(chainRate)}
                caption={`${fmtNum(t.chain_failed)} of ${fmtNum(t.chain_runs)} finished runs`}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatCard
                label="Error types"
                value={fmtNum(data.groups.length + data.more_groups)}
                caption={data.kinds[0] ? `Most common: ${KIND_META[data.kinds[0].kind].label}` : 'None'}
              />
            </Grid>
          </Grid>

          {data.kinds.length > 0 && (
            <Stack direction="row" spacing={0.75} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 0.75 }} alignItems="center">
              <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                Filter:
              </Typography>
              {data.kinds.map(k => (
                <KindChip key={k.kind} kind={k.kind} count={k.count} selected={kind === k.kind} onClick={() => setKind(kind === k.kind ? null : k.kind)} />
              ))}
            </Stack>
          )}

          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <SectionTitle
              title="Failures by error type"
              help={`Largest first. Share is of all failed runs in the window. Bars are ${data.window.unit === 'hour' ? 'hourly' : 'daily'} (UTC).`}
            />
            {t.failed === 0 ? (
              <Alert severity="success">No failed runs in this window.</Alert>
            ) : groups.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No failures of this type in the top groups.
              </Typography>
            ) : (
              <GroupTable groups={groups} axisLabels={axisLabels} customerId={customerId} unit="failed runs" countLabel="Failed runs" />
            )}
            {data.more_groups > 0 && !kind && (
              <Typography variant="caption" color="text.secondary">
                {fmtNum(data.more_groups)} smaller groups not shown.
              </Typography>
            )}
          </Paper>

          {data.rescued && (
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <SectionTitle
                title="Rescued failures"
                help="Successful function runs where at least one model call failed and a retry or backup model answered. Each run counts once per error type. Usage-only runs keep the failure type but not the message."
              />
              {data.rescued.runs === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No successful run needed a retry or backup model in this window.
                </Typography>
              ) : (
                <>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {fmtNum(data.rescued.runs)} successful runs recovered from at least one failed model call.
                  </Typography>
                  {rescuedGroups.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No rescued failures of this type.
                    </Typography>
                  ) : (
                    <GroupTable groups={rescuedGroups} axisLabels={axisLabels} customerId={customerId} unit="runs" countLabel="Runs" />
                  )}
                </>
              )}
            </Paper>
          )}
        </>
      )}
    </Box>
  );
};

export default ErrorExplorer;
